import Jsona from 'jsona';
import { orderBy } from 'lodash';
import request, { PaginationOptions } from './request';
import deserialize from './deserialize';
import { generate } from 'generate-password';

export type Member = {
  id: string;
  name: string;
  totalStamps: number;
  createdAt: number;
  email: string;
  mobNumber: string;
  user: { id: string };
  loyaltyProgram: { id: string };
  notes?: string;
};

export type MemberListing = {
  id: string;
  name: string;
  totalStamps: number;
  createdAt: number;
  email: string;
  mobNumber: string;
  user: { id: string };
  loyaltyProgram: { id: string };
};

export type Pagination = {
  page: number;
  per: number;
  count: number;
  total: number;
};

export type User = {
  id: string;
  createdAt: number;
  dob: number;
  email: string;
  altEmail: string;
  firstName: string;
  lastName: string;
  gender: string;
  kycStatus: string;
  referralCode: string;
  status: string;
  mobNum: string;
};

export type MemberList = {
  members: Member[];
  pagination: Pagination;
};

export function getUser(companyId: string, memberId: string) {
  let url = `companion/v1/companies/${companyId}/member/${memberId}`;

  return request<User>({
    url,
    method: 'GET',
    transformResponse: deserialize,
  });
}

export function getMember(companyId: string, memberId: string) {
  let url = `companion/v1/companies/${companyId}/membership/${memberId}`;

  return request<Member>({
    url,
    method: 'GET',
    transformResponse: deserialize,
  });
}

export function getMemberByUserID(companyId: string, userID: string) {
  let url = `companion/v1/companies/${companyId}/users/${userID}/membership`;

  return request<Member>({
    url,
    method: 'GET',
    transformResponse: deserialize,
  });
}

export function listFull(companyId: string) {
  let url = `companion/v1/companies/${companyId}/members/full`;

  return request<MemberListing[]>({
    url,
    method: 'GET',
    transformResponse: deserialize,
  });
}

export function searchList(
  companyId: string,
  searchValue: string,
  opts?: PaginationOptions
) {
  let url = `companion/v1/companies/${companyId}/members`;
  if (opts) {
    const params = new URLSearchParams({
      page: opts.page.toString(),
      per: opts.per.toString(),
      q: searchValue,
    });

    url = `${url}?${params.toString()}&sort=firstName`;
  }

  return request<MemberList>({
    url,
    method: 'GET',
    transformResponse: deserializeMember,
  });
}

export function list(companyId: string, opts?: PaginationOptions) {
  let url = `companion/v1/companies/${companyId}/members`;
  if (opts) {
    const params = new URLSearchParams({
      page: opts.page.toString(),
      per: opts.per.toString(),
      sort: 'firstName',
    });

    url = `${url}?${params.toString()}`;
  }

  return request<MemberList>({
    url,
    method: 'GET',
    transformResponse: deserializeMember,
  });
}

export function deserializeMember(data: string) {
  if (typeof data === 'string') {
    try {
      const body = JSON.parse(data);

      if (body.errors) {
        return body.errors;
      }

      const formatter = new Jsona();

      const deserialized = formatter.deserialize(data);
      const { page, per, count, total } = body.meta;

      return {
        members: orderBy(deserialized, ['createdAt'], ['desc']),
        pagination: {
          page,
          per,
          count,
          total,
        },
      };
    } catch (e) {
      /* Ignore */
    }
  }
  return data;
}

interface CreateAttrs {
  userID: string;
  companyId: string;
}

export function create(attrs: CreateAttrs) {
  const { userID, companyId } = attrs;

  return request<Member[]>({
    url: `/companion/v1/companies/${companyId}/membership`,
    method: 'POST',
    data: {
      data: {
        type: 'memberships',
        attributes: {
          userID,
        },
      },
    },
    transformResponse: deserialize,
  });
}

interface CreateMemberAttrs {
  firstName: string;
  lastName: string;
  email?: string;
  mobNum?: string;
  companyId: string;
}

export function createUserWithEmail(attrs: CreateMemberAttrs) {
  const { firstName, lastName, email, companyId } = attrs;
  let password = generate({
    length: 12,
    numbers: true,
    symbols: true,
  });
  return request<Member[]>({
    url: `/companion/v1/companies/${companyId}/membership/signup`,
    method: 'POST',
    data: {
      data: {
        type: 'signup',
        attributes: {
          firstName,
          lastName,
          email,
          password,
        },
      },
    },
    transformResponse: deserialize,
  });
}

export function createUserWithMobile(attrs: CreateMemberAttrs) {
  const { firstName, lastName, mobNum, companyId } = attrs;

  return request<Member[]>({
    url: `/companion/v1/companies/${companyId}/membership/signup`,
    method: 'POST',
    data: {
      data: {
        type: 'signup',
        attributes: {
          firstName,
          lastName,
          mobNum,
        },
      },
    },
    transformResponse: deserialize,
  });
}

export function createUserWithMobileAndEmail(attrs: CreateMemberAttrs) {
  const { firstName, lastName, mobNum, email, companyId } = attrs;

  return request<Member[]>({
    url: `/companion/v1/companies/${companyId}/membership/signup`,
    method: 'POST',
    data: {
      data: {
        type: 'signup',
        attributes: {
          firstName,
          lastName,
          mobNum,
          email,
        },
      },
    },
    transformResponse: deserialize,
  });
}

interface UpdateNotesAttrs {
  companyId: string;
  notes: string;
  memberID: string;
}

export function updateNotes(attrs: UpdateNotesAttrs) {
  const { companyId, notes, memberID } = attrs;

  return request<Member>({
    url: `/companion/v1/companies/${companyId}/membership/${memberID}`,
    method: 'PATCH',
    data: {
      data: {
        attributes: {
          notes,
        },
      },
    },
    transformResponse: deserialize,
  });
}

interface DeleteMemberAttrs {
  companyId: string;
  memberID: string;
}

export function deleteMember(attrs: DeleteMemberAttrs) {
  const { companyId, memberID } = attrs;

  return request<Member>({
    url: `/companion/v1/companies/${companyId}/membership/${memberID}`,
    method: 'DELETE',
    transformResponse: deserialize,
  });
}
